import { useRequestURL } from 'nuxt/app'

import { NOINDEX_NOFOLLOW } from '../../types/seo'
import type { SeoSiteProfile } from '../../types/seo'

/**
 * Safety net for `useSeo` when an app forgot `provideSeoSiteProfile(...)`.
 *
 * A missing provide is a bug, not a supported mode. This profile exists only so the render never
 * crashes and the half-configured page never reaches the index — it deliberately carries **no
 * branding**. Every app that renders SEO tags provides its own profile
 * (`createGokollabSeoSiteProfile`, `createClientPortalSeoSiteProfile`); reproducing their branding
 * here would mean maintaining a third copy of values that must not drift.
 *
 * The one thing worth resolving is the origin, so a canonical emitted in this state is still an
 * absolute URL pointing at the host that served the request.
 */

/** Request origin on the server, window origin in the browser, empty when neither is available. */
function resolveFallbackOrigin(): string {
  try {
    const origin = useRequestURL()?.origin
    if (origin && origin !== 'null') return origin.replace(/\/+$/, '')
  } catch {
    // Outside a Nuxt request context (client navigation, tests).
  }

  const browserOrigin = globalThis.location?.origin
  if (browserOrigin && browserOrigin !== 'null') return browserOrigin.replace(/\/+$/, '')

  return ''
}

/** Brand-free, never-indexed defaults. See the module docblock for why there is no per-app branch. */
export function createFallbackSeoSiteProfile(): SeoSiteProfile {
  return {
    siteName: '',
    baseUrl: resolveFallbackOrigin(),
    defaultDescription: '',
    defaultImage: { url: '', alt: '' },
    defaultRobots: NOINDEX_NOFOLLOW,
    defaultOgType: 'website',
    defaultTwitterCard: 'summary',
    formatTitle: pageTitle => pageTitle?.trim() ?? '',
  }
}
